import type { Pinia } from 'pinia'
import type { App, Component, Plugin } from 'vue'
import type { BaseStorage } from '../utils/storage'
import type { Platform } from '../utils/types'
import { createApp as createVue } from 'vue'
import { ErrorService } from '../diagnostics/error-service'
import { http } from '../http'
import { usePinia } from '../store'
import { useSession } from './session'

interface AppConfig {
  platform: Platform
  storage: BaseStorage

  /**
   * Installs necessary plugins to pinia context.
   * Creates a pinia context if required
   */
  pinia?: Pinia | false

  /**
   * Syncs default pinia stores.
   * @default true
   */
  sync?: boolean

  /** @default true */
  sentry?: boolean

  /** Vue router */
  router?: Plugin<[]>

  mount: string | false
}

export function initApp(app: App<Element>, config: AppConfig) {
  const {
    pinia,
    platform,
    router,
    mount,
    sentry = true,
    sync = true
  } = config

  // TODO: Track platform

  if (sentry)
    app.use(ErrorService, { platform })

  // Init pinia
  if (pinia !== false) {
    const plugin = usePinia(pinia)

    // Install pinia if not installed
    if (!app.config.globalProperties.$pinia)
      app.use(plugin)
  }

  if (router)
    app.use(router)

  // Assign http storage
  http.setStorage(config.storage)

  // Load session
  if (pinia && sync)
    useSession(pinia)

  if (mount)
    app.mount(mount)

  return app
}

export function createApp(component: Component<any>, config: AppConfig & { mount?: string }) {
  try {
    initApp(createVue(component), config)
  }
  catch (cause) {
    ErrorService.log(new Error('Error while initializing vue app', { cause }))
  }
}
